import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
/**
 * cart-footer — CONFIG-BY-CONFIG port of Shrine's cart-footer system. In Shrine the
 * `main-cart-footer.liquid` SECTION carries ZERO section-level settings; the cart footer is composed of
 * theme blocks inside a recursive `container--cart` orchestrator (Subtotals / Checkout button / Discount
 * field / Cart note / Policies checkbox / Checkpoints bar / Progress bar / Countdown timer). Our builder's
 * block model isn't the recursive Shrine container yet, so we INLINE every cart-footer block as a settings
 * cluster on this one section and render the full order summary from `resolveCart(context)` (demo cart in
 * the builder / offline). EVERY non-Shopify-platform Shrine block setting below is wired into the
 * Component render — a control that renders nothing is not implemented.
 *
 * Deferred = genuine Shopify-platform behaviour (each marked in its `info`):
 *  - LIVE cart mutations — apply-discount AJAX, checkout submit, cart.note write, qty/remove — wired to the
 *    backend cart layer later; the controls render now (same deferral as cod-buybox ATC / cart-items).
 *  - Material-Symbols glyph FONT (progress/checkpoints/checkout icons) → inline SVG instead.
 *  - Shopify image_picker / image_url assets (custom icons, prefix/suffix icons) → inline SVG / omitted.
 *  - `display_stripes` marquee ANIMATION, automatic-discount / subscription detection, Shopify color
 *    schemes (`color_scheme inverse`), `@app` injection, content_for_additional_checkout_buttons.
 *
 * className prefix in the Component: `aqx-ctf-`. Colors come from theme tokens only (hex only in a color
 * default). `templates: ['cart']` — this section lives on the cart page, below cart-items.
 */
const ri = (html) => ({ format: 'html', html });
// The five theme-color tokens Shrine offers on cart blocks (accent-1 / accent-2 / outline-button / text).
const TEXT_COLOR_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'outline-button', label: 'Outline button' },
    { value: 'text', label: 'Text' },
];
export const cartFooterSchema = {
    type: 'cart-footer',
    name: 'Cart footer',
    version: 1,
    templates: ['cart'],
    fields: [
        // ════════════════════════════════════════════════════════════════════════════
        // Subtotals (Shrine cart_subtotals.liquid — 'Subtotals')
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'displayTotalSavings',
            type: 'checkbox',
            label: 'Show total savings',
            group: 'Subtotals',
            default: false,
        },
        {
            id: 'savingsLeftText',
            type: 'richtext',
            label: 'Savings — left text',
            group: 'Subtotals',
            info: 'Token [savings] expands to the cart savings amount.',
            default: ri('<strong>Économies : </strong>'),
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'savingsRightText',
            type: 'richtext',
            label: 'Savings — right text',
            group: 'Subtotals',
            info: 'Token [savings] expands to the cart savings amount.',
            default: ri('<strong>-[savings]</strong>'),
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'savingsAlignment',
            type: 'select',
            label: 'Savings alignment',
            group: 'Subtotals',
            default: 'spaced',
            options: [
                { value: 'spaced', label: 'Spaced' },
                { value: 'center', label: 'Center' },
            ],
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'savingsTextColor',
            type: 'select',
            label: 'Savings text color',
            group: 'Subtotals',
            default: 'accent-1',
            options: TEXT_COLOR_OPTIONS,
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'savingsTextSize',
            type: 'range',
            label: 'Savings text size',
            group: 'Subtotals',
            default: 16,
            min: 10,
            max: 30,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'savingsPosition',
            type: 'select',
            label: 'Savings position',
            group: 'Subtotals',
            default: 'above',
            options: [
                { value: 'above', label: 'Above subtotal' },
                { value: 'bellow', label: 'Below subtotal' },
            ],
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'savingsSpacing',
            type: 'range',
            label: 'Savings spacing',
            group: 'Subtotals',
            default: 10,
            min: 0,
            max: 30,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'displayTotalSavings', equals: true },
        },
        {
            id: 'displaySubtotal',
            type: 'checkbox',
            label: 'Show subtotal',
            group: 'Subtotals',
            default: true,
        },
        {
            id: 'subtotalLeftText',
            type: 'richtext',
            label: 'Subtotal — left text',
            group: 'Subtotals',
            default: ri('<strong>Sous-total : </strong>'),
            visibleWhen: { field: 'displaySubtotal', equals: true },
        },
        {
            id: 'subtotalRightText',
            type: 'richtext',
            label: 'Subtotal — right text',
            group: 'Subtotals',
            info: 'Token [subtotal] expands to the cart subtotal.',
            default: ri('<strong>[subtotal]</strong>'),
            visibleWhen: { field: 'displaySubtotal', equals: true },
        },
        {
            id: 'subtotalAlignment',
            type: 'select',
            label: 'Subtotal alignment',
            group: 'Subtotals',
            default: 'spaced',
            options: [
                { value: 'spaced', label: 'Spaced' },
                { value: 'center', label: 'Center' },
            ],
            visibleWhen: { field: 'displaySubtotal', equals: true },
        },
        {
            id: 'subtotalTextColor',
            type: 'select',
            label: 'Subtotal text color',
            group: 'Subtotals',
            default: 'text',
            options: TEXT_COLOR_OPTIONS,
            visibleWhen: { field: 'displaySubtotal', equals: true },
        },
        {
            id: 'subtotalTextSize',
            type: 'range',
            label: 'Subtotal text size',
            group: 'Subtotals',
            default: 20,
            min: 10,
            max: 30,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'displaySubtotal', equals: true },
        },
        {
            id: 'displayDiscounts',
            type: 'checkbox',
            label: 'Show cart-level discounts',
            group: 'Subtotals',
            info: 'Cart-level discount applications are a Shopify cart object; the demo cart shows a sample applied discount.',
            default: true,
        },
        {
            id: 'displayProductDiscounts',
            type: 'checkbox',
            label: 'Show product discounts',
            group: 'Subtotals',
            default: true,
            visibleWhen: { field: 'displayDiscounts', equals: true },
        },
        {
            id: 'discountsLabel',
            type: 'richtext',
            label: 'Discounts label',
            group: 'Subtotals',
            default: ri('<strong>Réductions :</strong>'),
            visibleWhen: { field: 'displayDiscounts', equals: true },
        },
        {
            id: 'discountsAlignment',
            type: 'select',
            label: 'Discounts alignment',
            group: 'Subtotals',
            default: 'flex-start',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
            visibleWhen: { field: 'displayDiscounts', equals: true },
        },
        {
            id: 'subtotalsMarginTop',
            type: 'range',
            label: 'Subtotals — top margin',
            group: 'Subtotals',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
        },
        {
            id: 'subtotalsMarginBottom',
            type: 'range',
            label: 'Subtotals — bottom margin',
            group: 'Subtotals',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Checkout button (Shrine cart_checkout-btn.liquid — 'Checkout button')
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'showAdditionalCheckoutButtons',
            type: 'checkbox',
            label: 'Show dynamic checkout buttons',
            group: 'Checkout button',
            info: 'Shopify-only: content_for_additional_checkout_buttons (Shop Pay / PayPal express). Rendered as placeholder express buttons until the payment layer lands.',
            default: false,
        },
        {
            id: 'checkoutDisplayPrice',
            type: 'checkbox',
            label: 'Show price on button',
            group: 'Checkout button',
            default: false,
        },
        {
            id: 'checkoutEnableCustomColor',
            type: 'checkbox',
            label: 'Custom button color',
            group: 'Checkout button',
            default: false,
        },
        {
            id: 'checkoutCustomColor',
            type: 'color',
            label: 'Button color',
            group: 'Checkout button',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'checkoutEnableCustomColor', equals: true },
        },
        {
            id: 'checkoutIconScale',
            type: 'range',
            label: 'Icon scale',
            group: 'Checkout button',
            info: 'Scales the inline cart/lock icon (Shrine prefix_icon/suffix_icon image_picker assets are Shopify-only → inline SVG).',
            default: 120,
            min: 80,
            max: 180,
            step: 5,
            unit: '%',
        },
        {
            id: 'checkoutIconSpacing',
            type: 'range',
            label: 'Icon spacing',
            group: 'Checkout button',
            default: 10,
            min: 0,
            max: 40,
            step: 1,
            unit: 'px',
        },
        {
            id: 'checkoutMarginTop',
            type: 'range',
            label: 'Checkout — top margin',
            group: 'Checkout button',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
        },
        {
            id: 'checkoutMarginBottom',
            type: 'range',
            label: 'Checkout — bottom margin',
            group: 'Checkout button',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Discount field (Shrine cart_discount-field.liquid — 'Discount field')
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'discountEnabled',
            type: 'checkbox',
            label: 'Show discount field',
            group: 'Discount field',
            default: true,
        },
        {
            id: 'discountBottomSeparator',
            type: 'checkbox',
            label: 'Bottom separator',
            group: 'Discount field',
            default: true,
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountPlaceholder',
            type: 'text',
            label: 'Placeholder',
            group: 'Discount field',
            default: 'Entrez un code promo',
            defaultKey: 'cart.discount_code_placeholder',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountBtnLabel',
            type: 'text',
            label: 'Button label',
            group: 'Discount field',
            default: 'AJOUTER',
            defaultKey: 'cart.discount_add',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountErrorMsg',
            type: 'text',
            label: 'Empty-code error',
            group: 'Discount field',
            default: 'Veuillez saisir un code promo !',
            defaultKey: 'cart.discount_error_empty',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountErrorNotApplicableMsg',
            type: 'text',
            label: 'Not-applicable error',
            group: 'Discount field',
            info: 'Shown when the AJAX apply (Shopify discount engine) rejects the code. Wired to the backend cart layer later.',
            default: "Ce code n'est pas valide ou ne s'applique pas à votre panier.",
            defaultKey: 'cart.discount_error_not_applicable',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountErrorAlreadyAppliedMsg',
            type: 'text',
            label: 'Already-applied error',
            group: 'Discount field',
            default: 'Ce code est déjà appliqué à votre commande.',
            defaultKey: 'cart.discount_error_already_applied',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountSuccessMsg',
            type: 'text',
            label: 'Success message',
            group: 'Discount field',
            default: '',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountSuccessMsgDuration',
            type: 'range',
            label: 'Success message duration',
            group: 'Discount field',
            default: 3000,
            min: 500,
            max: 8000,
            step: 100,
            unit: 'ms',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountMarginTop',
            type: 'range',
            label: 'Discount — top margin',
            group: 'Discount field',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        {
            id: 'discountMarginBottom',
            type: 'range',
            label: 'Discount — bottom margin',
            group: 'Discount field',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'discountEnabled', equals: true },
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Cart note (Shrine cart_note.liquid — 'Cart note')
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'noteEnabled',
            type: 'checkbox',
            label: 'Show order note',
            group: 'Cart note',
            info: 'Writes cart.note (Shopify cart object) — the textarea renders now; the write is wired to the backend cart layer later.',
            default: true,
        },
        {
            id: 'noteMarginTop',
            type: 'range',
            label: 'Note — top margin',
            group: 'Cart note',
            default: 0,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'noteEnabled', equals: true },
        },
        {
            id: 'noteMarginBottom',
            type: 'range',
            label: 'Note — bottom margin',
            group: 'Cart note',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'noteEnabled', equals: true },
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Policies checkbox (Shrine cart_tnc-checkbox.liquid — 'Policies Checkbox')
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'tncEnabled',
            type: 'checkbox',
            label: 'Show terms & conditions checkbox',
            group: 'Policies checkbox',
            default: false,
        },
        {
            id: 'tncCheckboxColor',
            type: 'select',
            label: 'Checkbox color',
            group: 'Policies checkbox',
            default: 'text',
            options: TEXT_COLOR_OPTIONS,
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncCheckboxText',
            type: 'richtext',
            label: 'Checkbox text',
            group: 'Policies checkbox',
            default: ri("J'accepte les conditions générales."),
            defaultKey: 'cart.terms_accept',
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncCheckboxTextSize',
            type: 'range',
            label: 'Checkbox text size',
            group: 'Policies checkbox',
            default: 17,
            min: 12,
            max: 22,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncCheckboxAlignment',
            type: 'select',
            label: 'Checkbox alignment',
            group: 'Policies checkbox',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncWarningPosition',
            type: 'select',
            label: 'Warning position',
            group: 'Policies checkbox',
            default: 'under-button',
            options: [
                { value: 'under-checkbox', label: 'Under checkbox' },
                { value: 'above-button', label: 'Above button' },
                { value: 'under-button', label: 'Under button' },
            ],
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncWarningText',
            type: 'richtext',
            label: 'Warning text',
            group: 'Policies checkbox',
            default: ri('Vous devez accepter les conditions générales.'),
            defaultKey: 'cart.terms_required',
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncWarningTextSize',
            type: 'range',
            label: 'Warning text size',
            group: 'Policies checkbox',
            default: 14,
            min: 12,
            max: 22,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncWarningAlignment',
            type: 'select',
            label: 'Warning alignment',
            group: 'Policies checkbox',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncWarningTextColor',
            type: 'color',
            label: 'Warning text color',
            group: 'Policies checkbox',
            default: { ref: 'custom', value: '#ff0000' },
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncDisableCheckoutButton',
            type: 'checkbox',
            label: 'Disable checkout until agreed',
            group: 'Policies checkbox',
            default: false,
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncMarginTop',
            type: 'range',
            label: 'Terms — top margin',
            group: 'Policies checkbox',
            default: 0,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        {
            id: 'tncMarginBottom',
            type: 'range',
            label: 'Terms — bottom margin',
            group: 'Policies checkbox',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'tncEnabled', equals: true },
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Progress bar (Shrine cart_progress-bar.liquid — 'Progress bar', single goal)
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'progressEnabled',
            type: 'checkbox',
            label: 'Show progress bar',
            group: 'Progress bar',
            info: 'Single-goal reward bar. Relies on Shopify automatic discounts to actually grant the reward — the bar UI renders against the cart subtotal/quantity.',
            default: false,
        },
        {
            id: 'progressGoalType',
            type: 'select',
            label: 'Goal type',
            group: 'Progress bar',
            default: 'subtotal',
            options: [
                { value: 'subtotal', label: 'Subtotal' },
                { value: 'quantity', label: 'Quantity' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressGoal',
            type: 'number',
            label: 'Goal amount',
            group: 'Progress bar',
            info: 'Subtotal goal is in the store currency (major units); quantity goal is an item count.',
            default: 50,
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressSubAutoComplete',
            type: 'checkbox',
            label: 'Auto-complete for subscriptions',
            group: 'Progress bar',
            info: 'Shopify-only: subscription detection. No render effect until the subscriptions layer lands.',
            default: false,
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressMessage',
            type: 'text',
            label: 'Progress message',
            group: 'Progress bar',
            info: 'Token [amount] expands to the remaining amount/quantity to the goal.',
            default: 'Plus que [amount] pour la livraison OFFERTE !',
            defaultKey: 'cart.free_shipping_progress',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressSuccessMessage',
            type: 'text',
            label: 'Success message',
            group: 'Progress bar',
            default: 'Bravo ! Vous bénéficiez de la livraison OFFERTE !',
            defaultKey: 'cart.free_shipping_reached',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressIconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Progress bar',
            default: 30,
            min: 16,
            max: 50,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressIconType',
            type: 'select',
            label: 'Icon type',
            group: 'Progress bar',
            info: 'Material-Symbols glyph + custom_icon image_picker are Shopify-only → "icon" renders an inline SVG; "custom_icon" falls back to the same SVG.',
            default: 'icon',
            options: [
                { value: 'icon', label: 'Icon' },
                { value: 'custom_icon', label: 'Custom icon' },
                { value: 'dot', label: 'Dot' },
                { value: 'none', label: 'None' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressIcon',
            type: 'select',
            label: 'Icon',
            group: 'Progress bar',
            info: 'Shrine cart_progress-bar exposes the goal glyph via a Material-Symbols name; the FONT is Shopify-only, so this picks among our 3 inline SVGs (it is the glyph CHOICE, not the font).',
            default: 'local_shipping',
            options: [
                { value: 'local_shipping', label: 'Shipping' },
                { value: 'sell', label: 'Discount tag' },
                { value: 'redeem', label: 'Gift' },
            ],
            visibleWhen: { field: 'progressIconType', equals: 'icon' },
        },
        {
            id: 'progressIconFilled',
            type: 'checkbox',
            label: 'Filled icon',
            group: 'Progress bar',
            default: false,
            visibleWhen: { field: 'progressIconType', equals: 'icon' },
        },
        {
            id: 'progressIconScale',
            type: 'range',
            label: 'Icon scale',
            group: 'Progress bar',
            default: 60,
            min: 30,
            max: 90,
            step: 1,
            unit: '%',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressIconColor',
            type: 'select',
            label: 'Icon color',
            group: 'Progress bar',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'outline-button', label: 'Outline button' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressIconCustomColor',
            type: 'color',
            label: 'Icon custom color',
            group: 'Progress bar',
            default: { ref: 'custom', value: '#DD1D1D' },
            visibleWhen: { field: 'progressIconColor', equals: 'custom' },
        },
        {
            id: 'progressIconBg',
            type: 'select',
            label: 'Icon background',
            group: 'Progress bar',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
                { value: 'custom_color', label: 'Custom color' },
                { value: 'gradient', label: 'Gradient' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressIconBgColor',
            type: 'color',
            label: 'Icon background color',
            group: 'Progress bar',
            default: { ref: 'custom', value: '#ececec' },
            visibleWhen: { field: 'progressIconBg', equals: 'custom_color' },
        },
        {
            id: 'progressIconBgGradient',
            type: 'gradient',
            label: 'Icon background gradient',
            group: 'Progress bar',
            default: 'linear-gradient(180deg, #ececec 0%, #cccccc 100%)',
            visibleWhen: { field: 'progressIconBg', equals: 'gradient' },
        },
        {
            id: 'progressBorderThickness',
            type: 'range',
            label: 'Icon border thickness',
            group: 'Progress bar',
            default: 2,
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressBorderStyle',
            type: 'select',
            label: 'Icon border style',
            group: 'Progress bar',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressBorderColor',
            type: 'select',
            label: 'Icon border color',
            group: 'Progress bar',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'outline-button', label: 'Outline button' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressBorderCustomColor',
            type: 'color',
            label: 'Icon border custom color',
            group: 'Progress bar',
            default: { ref: 'custom', value: '#DD1D1D' },
            visibleWhen: { field: 'progressBorderColor', equals: 'custom' },
        },
        {
            id: 'progressIconCornerRadius',
            type: 'range',
            label: 'Icon corner radius',
            group: 'Progress bar',
            default: 100,
            min: 0,
            max: 100,
            step: 1,
            unit: '%',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressLineHeight',
            type: 'range',
            label: 'Bar height',
            group: 'Progress bar',
            default: 9,
            min: 2,
            max: 15,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressDisplayStripes',
            type: 'checkbox',
            label: 'Display animated stripes',
            group: 'Progress bar',
            info: 'Renders a striped fill. Shrine animates the stripes (marquee); the animation is deferred — the static stripe pattern renders.',
            default: true,
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressLineColor',
            type: 'select',
            label: 'Bar fill color',
            group: 'Progress bar',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'outline-button', label: 'Outline button' },
                { value: 'text', label: 'Text' },
                { value: 'custom_color', label: 'Custom color' },
                { value: 'gradient', label: 'Gradient' },
            ],
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressLineCustomColor',
            type: 'color',
            label: 'Bar fill custom color',
            group: 'Progress bar',
            default: { ref: 'custom', value: '#DD1D1D' },
            visibleWhen: { field: 'progressLineColor', equals: 'custom_color' },
        },
        {
            id: 'progressLineGradient',
            type: 'gradient',
            label: 'Bar fill gradient',
            group: 'Progress bar',
            default: 'linear-gradient(90deg, #DD1D1D 0%, #ff7a7a 100%)',
            visibleWhen: { field: 'progressLineColor', equals: 'gradient' },
        },
        {
            id: 'progressMarginTop',
            type: 'range',
            label: 'Progress — top margin',
            group: 'Progress bar',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        {
            id: 'progressMarginBottom',
            type: 'range',
            label: 'Progress — bottom margin',
            group: 'Progress bar',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'progressEnabled', equals: true },
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Checkpoints bar (Shrine cart_checkpoints-bar.liquid — 'Checkpoints bar', 3 goals)
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'checkpointsEnabled',
            type: 'checkbox',
            label: 'Show checkpoints bar',
            group: 'Checkpoints bar',
            info: 'Multi-goal reward bar (up to 3 milestones). Relies on Shopify automatic discounts to grant rewards; the milestone UI renders against the cart subtotal/quantity.',
            default: false,
        },
        {
            id: 'checkpointsGoalType',
            type: 'select',
            label: 'Goal type',
            group: 'Checkpoints bar',
            default: 'subtotal',
            options: [
                { value: 'subtotal', label: 'Subtotal' },
                { value: 'quantity', label: 'Quantity' },
            ],
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsProgressMessage',
            type: 'text',
            label: 'Progress message',
            group: 'Checkpoints bar',
            info: 'Tokens [amount] and [next_goal] expand to the remaining amount and the next milestone label.',
            default: 'Plus que [amount] pour débloquer [next_goal] !',
            defaultKey: 'cart.checkpoints_progress',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsSuccessMessage',
            type: 'text',
            label: 'Success message',
            group: 'Checkpoints bar',
            default: 'Bravo ! Vous avez tout débloqué !',
            defaultKey: 'cart.checkpoints_all_reached',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsLabelsMobileTextSize',
            type: 'range',
            label: 'Labels text size (mobile)',
            group: 'Checkpoints bar',
            default: 10,
            min: 6,
            max: 20,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsLabelsDesktopTextSize',
            type: 'range',
            label: 'Labels text size (desktop)',
            group: 'Checkpoints bar',
            default: 12,
            min: 6,
            max: 24,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsIconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Checkpoints bar',
            default: 24,
            min: 16,
            max: 50,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsIconScale',
            type: 'range',
            label: 'Icon scale',
            group: 'Checkpoints bar',
            default: 60,
            min: 30,
            max: 90,
            step: 1,
            unit: '%',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsIconColor',
            type: 'select',
            label: 'Icon color',
            group: 'Checkpoints bar',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'outline-button', label: 'Outline button' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
            ],
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsIconCustomColor',
            type: 'color',
            label: 'Icon custom color',
            group: 'Checkpoints bar',
            default: { ref: 'custom', value: '#DD1D1D' },
            visibleWhen: { field: 'checkpointsIconColor', equals: 'custom' },
        },
        {
            id: 'checkpointsIconBg',
            type: 'select',
            label: 'Icon background',
            group: 'Checkpoints bar',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
                { value: 'custom_color', label: 'Custom color' },
                { value: 'gradient', label: 'Gradient' },
            ],
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsIconBgColor',
            type: 'color',
            label: 'Icon background color',
            group: 'Checkpoints bar',
            default: { ref: 'custom', value: '#ececec' },
            visibleWhen: { field: 'checkpointsIconBg', equals: 'custom_color' },
        },
        {
            id: 'checkpointsIconBgGradient',
            type: 'gradient',
            label: 'Icon background gradient',
            group: 'Checkpoints bar',
            default: 'linear-gradient(180deg, #ececec 0%, #cccccc 100%)',
            visibleWhen: { field: 'checkpointsIconBg', equals: 'gradient' },
        },
        {
            id: 'checkpointsBorderThickness',
            type: 'range',
            label: 'Icon border thickness',
            group: 'Checkpoints bar',
            default: 2,
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsBorderStyle',
            type: 'select',
            label: 'Icon border style',
            group: 'Checkpoints bar',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsBorderColor',
            type: 'select',
            label: 'Icon border color',
            group: 'Checkpoints bar',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'outline-button', label: 'Outline button' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
            ],
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsBorderCustomColor',
            type: 'color',
            label: 'Icon border custom color',
            group: 'Checkpoints bar',
            default: { ref: 'custom', value: '#DD1D1D' },
            visibleWhen: { field: 'checkpointsBorderColor', equals: 'custom' },
        },
        {
            id: 'checkpointsIconCornerRadius',
            type: 'range',
            label: 'Icon corner radius',
            group: 'Checkpoints bar',
            default: 100,
            min: 0,
            max: 100,
            step: 1,
            unit: '%',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsLineHeight',
            type: 'range',
            label: 'Bar height',
            group: 'Checkpoints bar',
            default: 7,
            min: 2,
            max: 15,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsDisplayStripes',
            type: 'checkbox',
            label: 'Display animated stripes',
            group: 'Checkpoints bar',
            info: 'Static stripe pattern renders; the Shrine marquee animation is deferred.',
            default: true,
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsLineColor',
            type: 'select',
            label: 'Bar fill color',
            group: 'Checkpoints bar',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'outline-button', label: 'Outline button' },
                { value: 'text', label: 'Text' },
                { value: 'custom_color', label: 'Custom color' },
                { value: 'gradient', label: 'Gradient' },
            ],
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsLineCustomColor',
            type: 'color',
            label: 'Bar fill custom color',
            group: 'Checkpoints bar',
            default: { ref: 'custom', value: '#DD1D1D' },
            visibleWhen: { field: 'checkpointsLineColor', equals: 'custom_color' },
        },
        {
            id: 'checkpointsLineGradient',
            type: 'gradient',
            label: 'Bar fill gradient',
            group: 'Checkpoints bar',
            default: 'linear-gradient(90deg, #DD1D1D 0%, #ff7a7a 100%)',
            visibleWhen: { field: 'checkpointsLineColor', equals: 'gradient' },
        },
        // ── Goal 1 (enabled by default) ──
        {
            id: 'checkpointGoal1Enabled',
            type: 'checkbox',
            label: 'Goal 1',
            group: 'Checkpoints bar · Goal 1',
            default: true,
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointGoal1IconType',
            type: 'select',
            label: 'Icon type',
            group: 'Checkpoints bar · Goal 1',
            info: 'Material-Symbols glyph + custom_icon image_picker are Shopify-only → inline SVG.',
            default: 'icon',
            options: [
                { value: 'icon', label: 'Icon' },
                { value: 'custom_icon', label: 'Custom icon' },
                { value: 'dot', label: 'Dot' },
                { value: 'none', label: 'None' },
            ],
            visibleWhen: { field: 'checkpointGoal1Enabled', equals: true },
        },
        {
            id: 'checkpointGoal1Icon',
            type: 'select',
            label: 'Icon',
            group: 'Checkpoints bar · Goal 1',
            default: 'local_shipping',
            options: [
                { value: 'local_shipping', label: 'Shipping' },
                { value: 'sell', label: 'Discount tag' },
                { value: 'redeem', label: 'Gift' },
            ],
            visibleWhen: { field: 'checkpointGoal1IconType', equals: 'icon' },
        },
        {
            id: 'checkpointGoal1IconFilled',
            type: 'checkbox',
            label: 'Filled icon',
            group: 'Checkpoints bar · Goal 1',
            default: false,
            visibleWhen: { field: 'checkpointGoal1IconType', equals: 'icon' },
        },
        {
            id: 'checkpointGoal1Label',
            type: 'richtext',
            label: 'Label (locked)',
            group: 'Checkpoints bar · Goal 1',
            default: ri('Livraison offerte'),
            visibleWhen: { field: 'checkpointGoal1Enabled', equals: true },
        },
        {
            id: 'checkpointGoal1Label2',
            type: 'richtext',
            label: 'Label (unlocked)',
            group: 'Checkpoints bar · Goal 1',
            default: ri('Livraison offerte'),
            visibleWhen: { field: 'checkpointGoal1Enabled', equals: true },
        },
        {
            id: 'checkpointGoal1Text',
            type: 'text',
            label: 'Reward text',
            group: 'Checkpoints bar · Goal 1',
            info: 'Used in the [next_goal] token of the progress message.',
            default: 'la livraison offerte',
            defaultKey: 'cart.goal_free_shipping',
            visibleWhen: { field: 'checkpointGoal1Enabled', equals: true },
        },
        {
            id: 'checkpointGoal1Amount',
            type: 'number',
            label: 'Goal amount',
            group: 'Checkpoints bar · Goal 1',
            default: 30,
            visibleWhen: { field: 'checkpointGoal1Enabled', equals: true },
        },
        // ── Goal 2 (enabled by default) ──
        {
            id: 'checkpointGoal2Enabled',
            type: 'checkbox',
            label: 'Goal 2',
            group: 'Checkpoints bar · Goal 2',
            default: true,
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointGoal2IconType',
            type: 'select',
            label: 'Icon type',
            group: 'Checkpoints bar · Goal 2',
            default: 'icon',
            options: [
                { value: 'icon', label: 'Icon' },
                { value: 'custom_icon', label: 'Custom icon' },
                { value: 'dot', label: 'Dot' },
                { value: 'none', label: 'None' },
            ],
            visibleWhen: { field: 'checkpointGoal2Enabled', equals: true },
        },
        {
            id: 'checkpointGoal2Icon',
            type: 'select',
            label: 'Icon',
            group: 'Checkpoints bar · Goal 2',
            default: 'sell',
            options: [
                { value: 'local_shipping', label: 'Shipping' },
                { value: 'sell', label: 'Discount tag' },
                { value: 'redeem', label: 'Gift' },
            ],
            visibleWhen: { field: 'checkpointGoal2IconType', equals: 'icon' },
        },
        {
            id: 'checkpointGoal2IconFilled',
            type: 'checkbox',
            label: 'Filled icon',
            group: 'Checkpoints bar · Goal 2',
            default: false,
            visibleWhen: { field: 'checkpointGoal2IconType', equals: 'icon' },
        },
        {
            id: 'checkpointGoal2Label',
            type: 'richtext',
            label: 'Label (locked)',
            group: 'Checkpoints bar · Goal 2',
            default: ri('-20 %'),
            visibleWhen: { field: 'checkpointGoal2Enabled', equals: true },
        },
        {
            id: 'checkpointGoal2Label2',
            type: 'richtext',
            label: 'Label (unlocked)',
            group: 'Checkpoints bar · Goal 2',
            default: ri('-20 %'),
            visibleWhen: { field: 'checkpointGoal2Enabled', equals: true },
        },
        {
            id: 'checkpointGoal2Text',
            type: 'text',
            label: 'Reward text',
            group: 'Checkpoints bar · Goal 2',
            default: 'une réduction de 20 %',
            defaultKey: 'cart.goal_discount',
            visibleWhen: { field: 'checkpointGoal2Enabled', equals: true },
        },
        {
            id: 'checkpointGoal2Amount',
            type: 'number',
            label: 'Goal amount',
            group: 'Checkpoints bar · Goal 2',
            default: 50,
            visibleWhen: { field: 'checkpointGoal2Enabled', equals: true },
        },
        // ── Goal 3 (disabled by default) ──
        {
            id: 'checkpointGoal3Enabled',
            type: 'checkbox',
            label: 'Goal 3',
            group: 'Checkpoints bar · Goal 3',
            default: false,
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointGoal3IconType',
            type: 'select',
            label: 'Icon type',
            group: 'Checkpoints bar · Goal 3',
            default: 'icon',
            options: [
                { value: 'icon', label: 'Icon' },
                { value: 'custom_icon', label: 'Custom icon' },
                { value: 'dot', label: 'Dot' },
                { value: 'none', label: 'None' },
            ],
            visibleWhen: { field: 'checkpointGoal3Enabled', equals: true },
        },
        {
            id: 'checkpointGoal3Icon',
            type: 'select',
            label: 'Icon',
            group: 'Checkpoints bar · Goal 3',
            default: 'redeem',
            options: [
                { value: 'local_shipping', label: 'Shipping' },
                { value: 'sell', label: 'Discount tag' },
                { value: 'redeem', label: 'Gift' },
            ],
            visibleWhen: { field: 'checkpointGoal3IconType', equals: 'icon' },
        },
        {
            id: 'checkpointGoal3IconFilled',
            type: 'checkbox',
            label: 'Filled icon',
            group: 'Checkpoints bar · Goal 3',
            default: false,
            visibleWhen: { field: 'checkpointGoal3IconType', equals: 'icon' },
        },
        {
            id: 'checkpointGoal3Label',
            type: 'richtext',
            label: 'Label (locked)',
            group: 'Checkpoints bar · Goal 3',
            default: ri('Cadeau offert'),
            visibleWhen: { field: 'checkpointGoal3Enabled', equals: true },
        },
        {
            id: 'checkpointGoal3Label2',
            type: 'richtext',
            label: 'Label (unlocked)',
            group: 'Checkpoints bar · Goal 3',
            default: ri('Cadeau offert'),
            visibleWhen: { field: 'checkpointGoal3Enabled', equals: true },
        },
        {
            id: 'checkpointGoal3Text',
            type: 'text',
            label: 'Reward text',
            group: 'Checkpoints bar · Goal 3',
            default: 'un cadeau offert',
            defaultKey: 'cart.goal_free_gift',
            visibleWhen: { field: 'checkpointGoal3Enabled', equals: true },
        },
        {
            id: 'checkpointGoal3Amount',
            type: 'number',
            label: 'Goal amount',
            group: 'Checkpoints bar · Goal 3',
            default: 100,
            visibleWhen: { field: 'checkpointGoal3Enabled', equals: true },
        },
        {
            id: 'checkpointsMarginTop',
            type: 'range',
            label: 'Checkpoints — top margin',
            group: 'Checkpoints bar',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        {
            id: 'checkpointsMarginBottom',
            type: 'range',
            label: 'Checkpoints — bottom margin',
            group: 'Checkpoints bar',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'checkpointsEnabled', equals: true },
        },
        // ════════════════════════════════════════════════════════════════════════════
        // Countdown timer (Shrine cart_countdown-timer.liquid — 'Countdown timer')
        // ════════════════════════════════════════════════════════════════════════════
        {
            id: 'timerEnabled',
            type: 'checkbox',
            label: 'Show countdown timer',
            group: 'Countdown timer',
            default: false,
        },
        {
            id: 'timerText',
            type: 'richtext',
            label: 'Timer text',
            group: 'Countdown timer',
            info: 'Token [timer] expands to the live mm:ss countdown.',
            default: ri('<strong>Panier réservé encore [timer] minutes !</strong>'),
            visibleWhen: { field: 'timerEnabled', equals: true },
        },
        {
            id: 'timerDuration',
            type: 'number',
            label: 'Duration (seconds)',
            group: 'Countdown timer',
            default: 300,
            visibleWhen: { field: 'timerEnabled', equals: true },
        },
        {
            id: 'timerFontSize',
            type: 'select',
            label: 'Font size',
            group: 'Countdown timer',
            default: '1.4',
            options: [
                { value: '1', label: 'Small' },
                { value: '1.2', label: 'Medium' },
                { value: '1.4', label: 'Large' },
                { value: '1.6', label: 'X-Large' },
                { value: '1.8', label: 'XX-Large' },
            ],
            visibleWhen: { field: 'timerEnabled', equals: true },
        },
        {
            id: 'timerColorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Countdown timer',
            info: 'Shopify theme color schemes; mapped to our section scheme tokens (accent / background / inverse).',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
            visibleWhen: { field: 'timerEnabled', equals: true },
        },
        {
            id: 'timerMarginTop',
            type: 'range',
            label: 'Timer — top margin',
            group: 'Countdown timer',
            default: 0,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'timerEnabled', equals: true },
        },
        {
            id: 'timerMarginBottom',
            type: 'range',
            label: 'Timer — bottom margin',
            group: 'Countdown timer',
            default: 15,
            min: 0,
            max: 45,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'timerEnabled', equals: true },
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: scheme + padding + width) ─────────────────────────
        ...universalFields,
    ],
    blocks: [],
};
export const cartFooterPresets = [
    {
        name: 'Cart footer',
        settings: {
            // Subtotals
            displayTotalSavings: true,
            savingsTextColor: 'accent-1',
            displaySubtotal: true,
            subtotalTextColor: 'text',
            displayDiscounts: true,
            displayProductDiscounts: true,
            // Discount field
            discountEnabled: true,
            discountBottomSeparator: true,
            // `discountPlaceholder` and `discountBtnLabel` were repeated here, identical to the fields' own
            // defaults. A preset is spread OVER the defaults, so the copies would have overwritten the
            // translated values and put French back into an Arabic shop. Same for the two progress messages.
            // Checkout button
            checkoutDisplayPrice: true,
            checkoutIconScale: 120,
            // Cart note + terms
            noteEnabled: true,
            tncEnabled: true,
            tncDisableCheckoutButton: true,
            // Progress bar (single goal) — a realistic free-shipping bar on by default
            progressEnabled: true,
            progressGoalType: 'subtotal',
            progressGoal: 600,
            progressIcon: 'local_shipping',
            width: 'narrow',
        },
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const cartFooterMeta = {
    label: 'Cart footer',
    group: 'Cart',
    icon: 'cart',
};
//# sourceMappingURL=schema.js.map